'use client';

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useSession, signIn, signOut } from 'next-auth/react';

import { _userData } from '@core/market-data-sdk';
import useSWR from 'swr';
import { TeamMemberRole } from '../utils/user-roles';
import { IProUserProfileResponse } from '@core/market-data-sdk';

interface AuthProviderContextProps {
  initialized: boolean;
  userIsLogged: boolean;
  userName: string | undefined;
  userEmail: string | undefined;
  displayEmail: string | undefined;
  teamId?: string;
  accessToken?: string;
  userIsPro: boolean;
  userIsProStarter: boolean;
  userIsPrime: boolean;
  userIsPremium: boolean;
  roles: TeamMemberRole[];
  proProfile?: IProUserProfileResponse;
  teamTheme?: {
    team_color: string;
    team_color_secondary: string;
    team_name: string;
    team_profile: string;
  };
  login: () => void;
  logout: () => Promise<void>;
}

export const AuthProviderContext = createContext({
  initialized: false,
  userIsLogged: false,
  userName: undefined,
  userEmail: undefined,
  displayEmail: undefined,
  accessToken: undefined,
  teamId: undefined,
  userIsPro: false,
  userIsProStarter: false,
  userIsPrime: false,
  userIsPremium: false,
  roles: [],
  teamTheme: undefined,
  login: () => {},
  logout: () => Promise.resolve(),
} as AuthProviderContextProps);

/**
 * This provider must be used to wrap all components that need to access user.
 * Isolated from NextAuth, this provider is used to provide user data to the entire application.
 * @returns
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const sessionData = useSession();

  const { data: proProfile } = useSWR(
    sessionData?.data?.user?.isPro ? 'get-pro-user-profile' : null,
    () => _userData.user.getPROUserProfile()
  );

  const { data: teamTheme } = useSWR(
    proProfile?.teamId ? 'get-team-theme' : null,
    () => _userData.team.getTeamTheme({ teamId: proProfile!.teamId })
  );

  const login = (callbackUrl?: string) => {
    signIn('keycloak', { callbackUrl });
  };

  useEffect(() => {
    if (
      sessionData?.data?.error ||
      // Se o usuário está autenticado e não tem accessToken, devemos fazer logout
      (sessionData?.status === 'authenticated' &&
        !sessionData?.data?.user?.accessToken)
    ) {
      signOut();
    }
  }, [sessionData]);

  const logout = async (postRedirectUri = '/') => {
    try {
      // Chama a API de logout que fará o federated logout e limpeza de cookies
      const response = await fetch('/api/logout', {
        method: 'GET',
        credentials: 'include', // Para enviar os cookies incluindo mr_id_token
      });

      if (response.ok) {
        // Se o logout federated foi bem-sucedido, redireciona para o Keycloak
        const redirectUrl = await response.text();
        await signOut({ redirect: false });

        // Redireciona para o Keycloak federated logout
        window.location.href = redirectUrl;
        return;
      }
    } catch (error) {
      await signOut({ callbackUrl: postRedirectUri });
    }
  };

  const roles = useMemo<TeamMemberRole[]>(() => {
    let roles = (sessionData?.data?.user?.roles ?? []) as TeamMemberRole[];

    if (proProfile?.role) {
      roles.push(proProfile?.role as TeamMemberRole);
    }

    return roles;
  }, [proProfile, sessionData?.data?.user?.roles]);

  // const userSession = formatKeycloackSession(session?.user, status);
  // const account = formatKeycloakAndBffUserTeam(data as any, session?.user);

  return (
    <AuthProviderContext.Provider
      value={{
        initialized: sessionData?.status !== 'loading',
        userIsLogged: sessionData?.status === 'authenticated',
        userName: sessionData?.data?.user?.name,
        userEmail: sessionData?.data?.user?.email,
        accessToken: sessionData?.data?.user?.accessToken,
        userIsPro: sessionData?.data?.user?.isPro ?? false,
        userIsProStarter:
          sessionData?.data?.user?.roles?.includes(
            TeamMemberRole.PRO_STARTER
          ) ?? false,
        userIsPrime: sessionData?.data?.user?.isPrime ?? false,
        userIsPremium: sessionData?.data?.user?.isPremium ?? false,
        roles,
        login,
        logout,
        // PRO DATA
        teamTheme,
        displayEmail: sessionData?.data?.user?.email,
        teamId: proProfile?.teamId,
        proProfile,
      }}
    >
      {children}
    </AuthProviderContext.Provider>
  );
};

/**
 * This hook must be used to access user data.
 * Its necessary verify if initialized is true before use user data.
 * @returns AuthProviderContextProps
 */
export const useAuth = () => {
  const context = useContext(AuthProviderContext);

  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }

  return context;
};
